/**
 * Whether a textarea's caret sits on its last visual line, wrapped lines included. A textarea
 * can't report where its caret is drawn, so this lays the text out again in a hidden copy and
 * compares the height up to the caret with the height of the whole.
 */
export function isCaretOnLastLine(field: HTMLTextAreaElement): boolean {
  if (field.selectionStart !== field.selectionEnd) return false
  const style = getComputedStyle(field)
  const copy = document.createElement('div')
  Object.assign(copy.style, {
    position: 'absolute',
    visibility: 'hidden',
    width: `${field.clientWidth}px`,
    font: style.font,
    letterSpacing: style.letterSpacing,
    whiteSpace: 'pre-wrap',
    overflowWrap: style.overflowWrap,
    wordBreak: style.wordBreak,
  })
  // The zero-width space gives a trailing line break a line to measure.
  const heightOf = (text: string) => {
    copy.textContent = `${text}\u200b`
    return copy.offsetHeight
  }
  document.body.append(copy)
  try {
    return heightOf(field.value.slice(0, field.selectionEnd)) >= heightOf(field.value)
  } finally {
    copy.remove()
  }
}

/**
 * Whether a textarea's caret sits on its first visual line — the mirror of
 * `isCaretOnLastLine`, measured the same way so wrapped lines count.
 */
export function isCaretOnFirstLine(field: HTMLTextAreaElement): boolean {
  if (field.selectionStart !== field.selectionEnd) return false
  const style = getComputedStyle(field)
  const copy = document.createElement('div')
  Object.assign(copy.style, {
    position: 'absolute',
    visibility: 'hidden',
    width: `${field.clientWidth}px`,
    font: style.font,
    letterSpacing: style.letterSpacing,
    whiteSpace: 'pre-wrap',
    overflowWrap: style.overflowWrap,
    wordBreak: style.wordBreak,
  })
  document.body.append(copy)
  try {
    copy.textContent = `${field.value.slice(0, field.selectionStart)}\u200b`
    const toCaret = copy.offsetHeight
    copy.textContent = '\u200b'
    return toCaret <= copy.offsetHeight
  } finally {
    copy.remove()
  }
}
